import type { GeneratorKey } from './generators.ts';

/**
 * The seven Quick Generators written as JavaScript scripts: the desktop's
 * `DefaultGeneratorScripts` (Python), line for line, for a new web script to start from. Each
 * defines `generate()`, which a run calls and shows, ready to create as an entry.
 */
export const DEFAULT_GENERATOR_SCRIPTS: Readonly<Record<GeneratorKey, string>> = {
  NAME: `function generate() {
  const given = rand_choice(["Arden", "Bryn", "Coren", "Dessa", "Eamon", "Fenna", "Garrick", "Halsin",
                             "Ilya", "Joric", "Kessa", "Lorne", "Maren", "Niall", "Orys", "Perrin",
                             "Quenna", "Roran", "Selwyn", "Talia", "Ulric", "Vesna", "Wynne", "Yorick",
                             "Zara"]);
  const surname = rand_choice(["Ashford", "Blackwood", "Corvane", "Dunmoor", "Emberlyn", "Fairwind",
                               "Graythorn", "Hollowell", "Ironbrook", "Larkspur", "Merrow",
                               "Nightshade", "Oakhart", "Ravenscar", "Stormwake", "Thistledown",
                               "Vaelthorn", "Wintermere"]);
  return { title: \`\${given} \${surname}\`, description: "" };
}
`,

  CITY: `function generate() {
  const prefix = rand_choice(["Silver", "Iron", "Wolf", "Raven", "Stone", "Storm", "Amber", "Shadow",
                              "Winter", "Sun", "Grey", "High", "Deep", "Thorn", "Moon"]);
  const suffix = rand_choice(["hold", "keep", "haven", "reach", "watch", "gate", "spire", "ford",
                              "crest", "fall", "mere", "hollow", "bridge"]);
  const name = prefix + suffix;
  const feature = rand_choice([
    "a fortified harbor that never freezes",
    "a market square built over an older, buried city",
    "walls quarried from a single vein of black stone",
    "a river that runs backward every spring flood",
    "seven bridges, each guarded by a different noble house",
    "a bell tower that rings on its own before disasters",
    "catacombs older than the city above them",
    "an academy famous for cartographers",
    "a standing garrison left over from a war two centuries gone",
    "terraced vineyards climbing the city's inner walls",
  ]);
  const population = 500 + rand_int(0, 59) * 500;
  return {
    title: name,
    population: population,
    notableFeature: feature,
    description: \`A settlement of roughly \${population} people, known for \${feature}.\`,
  };
}
`,

  NPC: `function generate() {
  const name = generate_name();
  const role = rand_choice(["innkeeper", "blacksmith", "hedge wizard", "caravan guard",
                            "retired soldier", "smuggler", "town clerk", "traveling bard", "herbalist",
                            "tomb robber", "temple acolyte", "harbor pilot", "moneylender",
                            "cartographer", "beast handler"]);
  const trait = rand_choice([
    "never makes eye contact", "counts everything out loud",
    "collects other people's debts", "speaks three languages badly",
    "always knows the fastest way out of a room", "is convinced they're cursed",
    "hums the same six notes when nervous", "keeps a knife nobody's ever seen used",
    "remembers every name they've ever heard", "flinches at the sound of bells",
  ]);
  const motivation = rand_choice([
    "paying off a debt they won't discuss", "protecting a family no one's met",
    "searching for someone who vanished years ago",
    "trying to buy passage out of the region",
    "quietly working against their own employer",
    "atoning for something done during the last war",
    "building a reputation to hide an old one",
    "waiting for a specific ship to come in",
  ]);
  return {
    title: name,
    role: role,
    trait: trait,
    motivation: motivation,
    description: \`A \${role} who \${trait}, secretly \${motivation}.\`,
  };
}
`,

  FACTION: `function generate() {
  const prefix = rand_choice(["The Order of", "The Covenant of", "The Free", "The Iron", "The Silent",
                              "The Last", "The Sworn", "The Hollow", "The Gilded", "The Wandering"]);
  const noun = rand_choice(["Flame", "Blade", "Compass", "Ledger", "Tide", "Crown", "Veil", "Hound",
                            "Anvil", "Star", "Root", "Chain"]);
  const name = \`\${prefix} \${noun}\`;
  const goal = rand_choice([
    "control of the region's trade routes",
    "the overthrow of a specific ruling house",
    "the protection of a secret no one else can be trusted with",
    "the recovery of something stolen generations ago",
    "independence from a distant empire",
    "the spread of a belief the established temples call heresy",
    "revenge for a betrayal only its oldest members remember",
  ]);
  const description = \`\${name} is a faction working toward \${goal}.\`;
  return { title: name, goal: goal, description: description };
}
`,

  TAVERN: `function generate() {
  const prefix = rand_choice(["The Prancing", "The Rusty", "The Drunken", "The Weary", "The Gilded",
                              "The Broken", "The Laughing", "The Salty", "The Wandering",
                              "The Crooked"]);
  const noun = rand_choice(["Pony", "Anchor", "Dragon", "Traveler", "Griffin", "Lantern", "Boar",
                            "Kettle", "Crow", "Barrel"]);
  const name = \`\${prefix} \${noun}\`;
  const specialty = rand_choice([
    "a spiced cider no one's been able to copy",
    "a back room where every deal in town eventually happens",
    "a house band that only plays after midnight",
    "the best (and only) hot meal for a day's ride",
    "a cellar rumored to connect to the old sewers",
    "a landlord who remembers every regular's usual order",
  ]);
  const atmosphere = rand_choice([
    "loud, smoky, and cheerfully overcrowded",
    "quiet enough to hear the fire crackle",
    "rough around the edges but fiercely loyal to its regulars",
    "surprisingly upscale for the neighborhood it's in",
    "the kind of place travelers are warned about first",
  ]);
  return {
    title: name,
    specialty: specialty,
    atmosphere: atmosphere,
    description: \`\${name} is known for \${specialty}. The atmosphere is \${atmosphere}.\`,
  };
}
`,

  HISTORY: `function capitalize(text) {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}

function generate() {
  const subject = rand_choice(["a founding", "a siege", "a plague", "a coronation", "a betrayal",
                               "a great fire", "a treaty", "a schism", "a discovery", "an uprising",
                               "a famine", "a first contact"]);
  const consequence = rand_choice([
    "reshaped the region's borders for a generation",
    "is still commemorated every year, for better or worse",
    "was covered up by those in power at the time",
    "gave rise to a faction that persists to this day",
    "is remembered very differently by each side involved",
    "left behind ruins that are still avoided",
    "changed the balance of power between two rival houses",
  ]);
  const title = \`\${capitalize(subject)} That \${capitalize(consequence)}\`;
  return { title: title, description: \`A historical event: \${subject} that \${consequence}.\` };
}
`,

  LANGUAGE: `function randomWord() {
  const syllables = ["va", "ren", "tho", "sil", "ka", "mir", "us", "en", "dol", "ith", "or", "an",
                     "qua", "zel", "yn"];
  const syllableCount = rand_int(2, 3);
  let word = "";
  for (let i = 0; i < syllableCount; i++) word += rand_choice(syllables);
  return word[0].toUpperCase() + word.slice(1);
}

function generate() {
  const adjective = rand_choice(["Old", "High", "Low", "Deep", "Wind", "Salt", "Stone", "Ash"]);
  const noun = rand_choice(["Tongue", "Speech", "Cant", "Dialect", "Rite-Speech"]);
  const name = \`\${adjective} \${noun}\`;
  const sampleWords = [];
  for (let i = 0; i < 5; i++) sampleWords.push(randomWord());
  return {
    title: name,
    sampleWords: sampleWords,
    description: \`A constructed language. Sample words: \${sampleWords.join(", ")}.\`,
  };
}
`,
};

/** A new script's starting point, when it doesn't start from a generator. */
export const NEW_SCRIPT_SOURCE = `// Scripts run in a sandbox: they can use generate_name(), generate_city() and the other
// generators, rand_choice(list), rand_int(min, max), roll("2d6 + 1") and log(...).
// create_city(...), create_npc(...) and create_entry(type, title, description) list the
// entries a run would make; you choose which to create.

for (let i = 0; i < 3; i++) {
  create_city(generate_city());
}
`;
